import { useMsal } from '@azure/msal-react';

import React, { useContext, useMemo } from 'react';
import { LoaderFunctionArgs, RouterProvider, createBrowserRouter, redirect } from 'react-router-dom';

import { AppContainer, MainLayout2 } from '@@containers';
import { ErrorBoundary, RouteErrorBoundary } from '@@components';
import {
	AppDesignerView,
	AgentsListView,
	AuditView,
	AppListView,
	ChangeSetsListView,
	ContentTemplateEditorView,
	ContentTemplateListView,
	ContextView,
	ConversationDetailView,
	ConversationListView,
	EnvironmentsView,
	FileDetailView,
	FileListView,
	FlowDesignerView,
	FlowListView,
	HomeView,
	OperationsWorkbench,
	PermissionAdministrationView,
	ProfileView,
	RegistrationView,
	RuleSetListView,
	TenantsListView,
	UsersListView,
} from '@@views';

import { UserClaims } from './common/UserClaims';
import { Permissions } from './common/Permissions';
import RequirePermission from './components/RequirePermission';
import { createRequireClaimLoader } from './common/routeLoaders';
import AuthContext from './components/AuthContext';
import AuthoringResourceDetailView from './views/AuthoringResources/AuthoringResourceDetailView';
import AuthoringResourcesView from './views/AuthoringResources/AuthoringResourcesView';
import ContextDetailView from './views/ContextDesigner/ContextDetailView';
import ContextListView from './views/ContextDesigner/ContextListView';
import ServiceListView from './views/ContextDesigner/ServiceListView';
import DashboardView from './views/Dashboard/DashboardView';
import { ViewLoader } from './views/FlowDesigner/Components/ViewLoader';
import IntegrationEventDesignerView from './views/IntegrationEventDesigner/IntegrationEventDesignerView';
import PlanningProjectDetailView from './views/Planning/PlanningProjectDetailView';
import PlanningProjectListView from './views/Planning/PlanningProjectListView';
import PlanningStoryDetailView from './views/Planning/PlanningStoryDetailView';
import ServiceConnectionsListView from './views/ServiceConnections/ServiceConnectionsListView';
import ServiceConnectionManualView from './views/ServiceConnections/ServiceConnectionManualView';

const RouteContainer: React.FC = () => {
	const auth = useContext(AuthContext);
	const { instance } = useMsal();

	// ToDo: Generate the routes from the menuItems config file
	const router = useMemo(() => {
		function protectedLoader({ request }: LoaderFunctionArgs) {
			// If the user is not logged in and tries to access `/protected`, we redirect
			// them to `/login` with a `from` parameter that allows login to redirect back
			// to this page upon successful authentication
			if (!auth.isAuthenticated) {
				const params = new URLSearchParams();
				params.set('from', new URL(request.url).pathname);
				return redirect(`/?${params.toString()}`);
			}
			return null;
		}

		return createBrowserRouter(
			[
				{
					id: 'root',
					path: '/',
					errorElement: <RouteErrorBoundary />,
					// loader() {
					//   // Our root route always provides the user, if logged in
					//   return { user: fakeAuthProvider.username };
					// },
					//loader: protectedLoader,
					Component: MainLayout2,
					HydrateFallback: ViewLoader,
					children: [
						{
							index: true,
							Component: HomeView,
						},
						{
							path: 'home',
							Component: HomeView,
						},
						{
							path: 'profile',
							loader: protectedLoader,
							Component: ProfileView,
						},
						{
							path: 'dashboard',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.DashboardRead}><DashboardView /></RequirePermission>,
						},
						{
							path: 'planning/projects',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ProjectRead}><PlanningProjectListView /></RequirePermission>,
						},
						{
							path: 'planning/projects/:projectId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ProjectRead}><PlanningProjectDetailView /></RequirePermission>,
						},
						{
							path: 'planning/projects/:projectId/stories/:storyId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ProjectRead}><PlanningStoryDetailView /></RequirePermission>,
						},
						{
							path: 'flow',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.FlowRead}><FlowListView /></RequirePermission>,
						},
						{
							path: 'flow/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.FlowRead}><FlowDesignerView /></RequirePermission>,
						},
						{
							path: 'template',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ContentRead}><ContentTemplateListView /></RequirePermission>,
						},
						{
							path: 'template/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ContentRead}><ContentTemplateEditorView /></RequirePermission>,
						},
						{
							path: 'file',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.FileRead}><FileListView /></RequirePermission>,
						},
						{
							path: 'file/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.FileRead}><FileDetailView /></RequirePermission>,
						},
						{
							path: 'conversation',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ConversationRead}><ConversationListView /></RequirePermission>,
						},
						{
							path: 'conversation/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ConversationRead}><ConversationDetailView /></RequirePermission>,
						},
						{
							path: 'rules',
							loader: protectedLoader,
							Component: RuleSetListView,
						},
						{
							path: 'context-designer',
							loader: () => redirect('/service'),
						},
						{
							path: 'service',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><ServiceListView /></RequirePermission>,
						},
						{
							path: 'service/resource',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'resources', loader: protectedLoader,
							element: <RequirePermission permissionIds={[Permissions.ServiceRead, Permissions.AppRead]} matchAny><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'resources/:id', loader: protectedLoader,
							element: <RequirePermission permissionIds={[Permissions.ServiceRead, Permissions.AppRead]} matchAny><AuthoringResourceDetailView /></RequirePermission>,
						},
						{
							path: 'app/:appId/resources', loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.AppRead}><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'service/resource/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><AuthoringResourceDetailView /></RequirePermission>,
						},
						{
							path: 'service/resource/context/:contextId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'service/:subDomainId/resource',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'service/:subDomainId/context/:contextId/resource',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><AuthoringResourcesView /></RequirePermission>,
						},
						{
							path: 'service/context/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><ContextView /></RequirePermission>,
						},
						{
							path: 'service/:id',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceRead}><ContextListView /></RequirePermission>,
						},
						{
							path: 'sub-domain',
							loader: () => redirect('/service'),
						},
						{
							path: 'sub-domain/:id',
							loader: ({ params }) => redirect(`/service/${params.id}`),
						},
						{
							path: 'context/:id',
							loader: protectedLoader,
							Component: ContextDetailView,
						},
						{
							path: 'operations-workbench',
							loader: protectedLoader,
							Component: OperationsWorkbench,
						},
						{
							path: 'service-connections/manual',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceConnectionRead}><ServiceConnectionManualView /></RequirePermission>,
						},
						{
							path: 'service-connections',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ServiceConnectionRead}><ServiceConnectionsListView /></RequirePermission>,
						},
						{
							path: 'environments',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.EnvironmentRead}><EnvironmentsView /></RequirePermission>,
						},
						{
							path: 'changesets',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.ChangeSetRead}><ChangeSetsListView /></RequirePermission>,
						},
						{
							path: 'tenants',
							loader: createRequireClaimLoader(instance, UserClaims.CanChangeTenantId),
							element: <TenantsListView />,
						},
						{
							path: 'audit',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.AuditRead}><AuditView /></RequirePermission>,
						},
						{
							path: 'agents',
							loader: createRequireClaimLoader(instance, UserClaims.CanChangeTenantId),
							element: <RequirePermission permissionId={Permissions.AgentRead}><AgentsListView /></RequirePermission>,
						},
						{
							path: 'users',
							loader: createRequireClaimLoader(instance, UserClaims.CanChangeTenantId),
							element: <RequirePermission permissionId={Permissions.UserRead}><UsersListView /></RequirePermission>,
						},
						{
							path: 'permissions',
							loader: protectedLoader,
							element: <RequirePermission permissionIds={[Permissions.PermissionRead, Permissions.RoleRead]}><PermissionAdministrationView /></RequirePermission>,
						},
						{
							path: 'app',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.AppRead}><AppListView /></RequirePermission>,
						},
						{
							path: 'app/:definitionId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.AppRead}><AppDesignerView /></RequirePermission>,
						},
						{
							path: 'app/:definitionId/version/:versionId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.AppRead}><AppDesignerView /></RequirePermission>,
						},
						{
							path: 'integration-events',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.IntegrationEventRead}><IntegrationEventDesignerView /></RequirePermission>,
						},
						{
							path: 'integration-events/:contextId',
							loader: protectedLoader,
							element: <RequirePermission permissionId={Permissions.IntegrationEventRead}><IntegrationEventDesignerView /></RequirePermission>,
						},
					],
				},
				{
					path: '/register',
					errorElement: <RouteErrorBoundary />,
					Component: RegistrationView,
				},
				{
					path: '/logout',
					async action() {
						// We signout in a "resource route" that we can hit from a fetcher.Form
						await auth.authService.signOut();
						return redirect('/');
					},
				},
			],
		);
	}, [auth, instance]);

	return <RouterProvider router={router} />;
};

const App: React.FC = () => (
	<AppContainer>
		<ErrorBoundary title="Portal unavailable" description="The portal could not be displayed. Try again to recover this session." fullHeight>
			<RouteContainer />
		</ErrorBoundary>
	</AppContainer>
);

export default App;

